/**
 * Analytics Integration - GTM, GA4, Meta Pixel, LinkedIn
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    fbq?: (...args: any[]) => void;
    _linkedin_data_partner_ids?: string[];
    _hsq?: any[];
  }
}

/**
 * Track page view (via GTM dataLayer)
 */
export function trackPageView(url: string): void {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'page_view',
    page_path: url,
    page_title: document.title,
  });
}

/**
 * Track custom event
 */
export function trackEvent(
  eventName: string,
  parameters?: Record<string, any>
): void {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    ...parameters,
  });
}

/**
 * Initialize HubSpot Tracking (EU Server - Direct)
 * Loads HubSpot directly from EU server for GDPR compliance
 * Note: This is third-party but necessary for lead tracking
 */
export function initHubSpotTracking(portalId: string): void {
  if (typeof window === 'undefined' || !portalId) return;
  
  // Skip if already loaded
  if (document.getElementById('hs-script-loader')) return;
  
  // Initialize HubSpot queue
  window._hsq = window._hsq || [];
  
  // Load HubSpot tracking script directly from EU server
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = `https://js-eu1.hs-scripts.com/${portalId}.js`;
  
  // Add to body for better performance
  document.body.appendChild(script);
}

/**
 * Initialize analytics based on consent
 * GTM loads always (cookieless), GA4/Pixels managed via GTM tags
 */
export function initializeAnalytics(consent: {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}): void {
  const hubspotPortalId = '26049307'; // HubSpot Portal ID
  
  // GTM already loaded in layout.tsx - just update consent
  // GTM tags will handle GA4, Meta, LinkedIn based on consent triggers
  
  // HubSpot still loads directly (needed for form integration)
  if (consent.analytics) {
    initHubSpotTracking(hubspotPortalId);
  }
}

/**
 * Track conversion event (form submission)
 * Supports Enhanced Conversions with proper consent checks
 */
export function trackConversion(
  conversionLabel?: string,
  enhancedConversionData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
    address?: {
      country?: string;
      postal_code?: string;
    };
  }
): void {
  if (typeof window === 'undefined') return;
  
  // Import consent helper dynamically to avoid circular dependencies
  const { getSavedConsent } = require('./consent');
  const consent = getSavedConsent();
  
  const analyticsGranted = consent?.analytics === true;
  const marketingGranted = consent?.marketing === true;
  
  // 1) Basis conversion event (ZONDER PII)
  // Alleen pushen bij analytics consent (want gebruikt door GA4)
  if (analyticsGranted) {
    trackEvent('conversion', {
      conversion_label: conversionLabel || 'demo_request',
      conversion_type: 'lead',
    });
  }
  
  // 2) Enhanced Conversions alleen bij marketing consent
  // Gebruik conditionele spread om alleen gevulde velden te pushen
  if (marketingGranted && enhancedConversionData) {
    trackEvent('conversion_enhanced', {
      conversion_label: conversionLabel || 'demo_request',
      conversion_type: 'lead',
      // PII velden (alleen als ze bestaan)
      ...(enhancedConversionData.email && { email: enhancedConversionData.email }),
      ...(enhancedConversionData.phone && { phone_number: enhancedConversionData.phone }),
      ...(enhancedConversionData.firstName && { first_name: enhancedConversionData.firstName }),
      ...(enhancedConversionData.lastName && { last_name: enhancedConversionData.lastName }),
      ...(enhancedConversionData.address && { address: enhancedConversionData.address }),
    });
  }
  
  // VERWIJDERD: Geen directe gtag/fbq calls meer
  // GTM tags handelen alle tracking af op basis van dataLayer events
}
